import { useState, useEffect, useRef, useMemo } from "react";
import { Table, Input, Select, DatePicker, Skeleton, Empty, Tooltip } from "antd";
import { SearchOutlined, FilterOutlined, ClearOutlined, CalendarOutlined, UserOutlined, InboxOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { selectLastInboxEvent } from "../../store/auth/authSlice";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import moment from "moment";
import * as XLSX from "xlsx";
import { Link, useNavigate } from "react-router-dom";
import EnquiryFollowUp from "./EnquiryFollowUp";
import Swal from "sweetalert2";
import {
  TeamOutlined, PlusOutlined, DeleteOutlined, UploadOutlined, DownloadOutlined,
} from "@ant-design/icons";
import { canTransferLeads, canViewAllLeads, currentUserId } from "../utils/role";
import { fetchExportEnquiries } from "../services/manageEnquiryService";
import { AdditionalPhones, phonesText, withCountryCode } from "../utils/phones";
import {
  hasDemo, DemoStatusPill, OverdueDemoHint, formatDemoWhen, isDemoOverdue,
  canJoinDemo, canRescheduleDemo, openTeamsMeeting,
} from "../utils/demo";
import RescheduleDemoModal from "./RescheduleDemoModal";
import {
  useInboxLeadsInfinite,
  useLeadDetailsFollowUp,
  useFollowUpOptions,
  useMyTeam,
  useAllocateLeads,
  useDeleteEnquiry,
  useImportEnquiries,
  useUpdateEnquiryStatus,
  useLeadUnreadCounts,
  useMarkLeadRead,
  LEAD_STATUS,
} from "../hooks/useManageEnquiry";

dayjs.extend(relativeTime);   // "2 days ago" on the last-contacted chip

/* The API stamps local (IST) timestamps with a trailing "Z", so parsing them as
   UTC shifts them another +5:30 — a mail sent at 11:11 AM read "4:41 PM", i.e.
   "in 5 hours", in the future. The suffix is dropped and the value is read as the
   wall-clock time it actually is. */
const apiTime = (v) =>
  dayjs(String(v ?? "").replace("T", " ").replace(/(\.\d+)?Z$/, ""));

/* Ordinary pages — the API filters by column and due-date and returns the real
   counts, so there is no need to pull the whole set into the browser any more.
   The board asks for more per page simply because it fills five columns. */
const LIST_PAGE = 20;
const BOARD_PAGE = 50;

/**
 * A lead's `status` is an `option_value` from the lead_follow_up_status master
 * (0 Follow Up … 7 Converted … 10 Negative), so the LABEL must come from that
 * master — never from a hardcoded map here, which drifts the moment someone
 * renames or adds an option in the DB.
 *
 * Only the colours stay local, keyed by the same option_value.
 */
const FOLLOWUP_STATUS_STYLE = {
  "0":  { bg: "#eff6ff",  color: "#2563eb", border: "#bfdbfe" },
  "1":  { bg: "#fff1f2",  color: "#e11d48", border: "#fecdd3" },
  "2":  { bg: "#f5f5f5",  color: "#6b7280", border: "#d1d5db" },
  "3":  { bg: "#f0fdf4",  color: "#16a34a", border: "#bbf7d0" },
  "4":  { bg: "#fffbeb",  color: "#d97706", border: "#fde68a" },
  "5":  { bg: "#fef2f2",  color: "#dc2626", border: "#fecaca" },
  "6":  { bg: "#f5f3ff",  color: "#7c3aed", border: "#ddd6fe" },
  "7":  { bg: "#ecfdf5",  color: "#059669", border: "#6ee7b7" },
  "10": { bg: "#fef2f2",  color: "#dc2626", border: "#fecaca" },
};

const EMPTY_FILTER = { search: "", start_date: null, end_date: null, stage: "", assign_to: "" };

/* Same board as All Leads (manageenquiry.jsx) — only the data differs: this one
   is scoped to the leads assigned to the logged-in user. */
const KANBAN_COLUMNS = [
  { key: "new",       label: "New",       color: "#6366f1", border: "#c7d2fe", bg: "#eef2ff" },
  { key: "contacted", label: "Contacted", color: "#0891b2", border: "#bae6fd", bg: "#e0f2fe" },
  { key: "future",    label: "Future",    color: "#d97706", border: "#fde68a", bg: "#fffbeb" },
  { key: "done",      label: "Done",      color: "#16a34a", border: "#86efac", bg: "#dcfce7" },
  { key: "negative",  label: "Negative",  color: "#dc2626", border: "#fca5a5", bg: "#fee2e2" },
];

/* Shared look for the header action buttons (Import / Export / Delete / Add) */
const toolbarBtn = {
  display: "inline-flex", alignItems: "center", gap: 6,
  borderRadius: 8, padding: "6px 14px", fontWeight: 600, fontSize: 12,
  border: "none", cursor: "pointer", marginLeft: 4, whiteSpace: "nowrap",
};

/* Column keys the API can send in `kanban_column`. */
const KANBAN_KEYS = new Set(KANBAN_COLUMNS.map((c) => c.key));
/* List view is ordered by stage, in the same left-to-right order as the board —
   New first, Negative last — so both views read the same way. */
const STAGE_RANK = Object.fromEntries(KANBAN_COLUMNS.map((c, i) => [c.key, i]));

const thStyle = {
  fontSize: "12px", fontWeight: 700, color: "#475569",
  textTransform: "uppercase", letterSpacing: "0.6px", whiteSpace: "nowrap",
};

export default function AssignedLeads() {
  const [filter, setFilter]                 = useState({ ...EMPTY_FILTER });
  const [appliedFilter, setAppliedFilter]   = useState({ ...EMPTY_FILTER });
  const [filtersOpen, setFiltersOpen]       = useState(true);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [followUpEnquiry, setFollowUpEnquiry] = useState(null);
  const [dueFilter, setDueFilter] = useState("all");     // "all" | "due" | "untouched"
  const [viewMode, setViewMode]   = useState("kanban");  // "kanban" | "list"

  /* ── Transfer leads (super-admin / admin / manager only) ── */
  const mayTransfer = canTransferLeads();
  /* Delete and Transfer stay with super-admin / admin / manager — one destroys
     data, the other moves someone else's lead. Import and Add Lead are open to
     everyone: a sales executive brings in their own leads. */
  const mayManage   = canTransferLeads();   // delete / transfer only
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferTo, setTransferTo]               = useState(null);
  const [selectedIds, setSelectedIds]             = useState([]);
  /* One ticking mode at a time: "transfer" | "delete" | "export" | null.
     Cards have no checkboxes, so entering any mode drops into List view. */
  const [selectMode, setSelectMode] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [file, setFile]             = useState(null);
  const [fileError, setFileError]   = useState("");
  const [exportLoading, setExportLoading] = useState(false);

  /* The Inbox covers the logged-in user AND everyone reporting to them, so the
     "Internal User" filter lists that team only — not every internal user. */
  const { data: teamMembers = [] } = useMyTeam();
  const seesEveryone = canViewAllLeads();
  const teamIds = useMemo(
    () => teamMembers.map((u) => u.id).filter(Boolean).join(","),
    [teamMembers],
  );

  const transferLeads = useAllocateLeads();
  const deleteEnquiry = useDeleteEnquiry();
  const importMutation = useImportEnquiries();
  const updateStatus = useUpdateEnquiryStatus();
  const navigate = useNavigate();
  const debounceRef = useRef(null);
  // Lead whose demo is being moved — null when the modal is closed.
  const [reschedulingLead, setReschedulingLead] = useState(null);

  /* ── search debounce ── */
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setAppliedFilter((p) => ({ ...p, search: filter.search }));
          }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [filter.search]);

  /* The API decides WHOSE leads come back (token → role → reporting line). This
     is only the "narrow to one person" filter, so it is empty unless the user
     picks someone. */
  const assignScope = appliedFilter.assign_to ? String(appliedFilter.assign_to) : "";

  /* ── data ──
     useInfiniteQuery keeps every loaded page in one cache entry, so invalidating
     it refetches ALL of them. The hand-rolled page counter refreshed only the
     page the user happened to be on, which is why a lead edited in the panel kept
     its old stage on earlier pages until a hard reload. */
  const {
    data,
    isFetching,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInboxLeadsInfinite({
    search:      appliedFilter.search,
    assign_to:   assignScope,
    start_date:  appliedFilter.start_date || "",
    end_date:    appliedFilter.end_date   || "",
    // Both are server filters now.
    /* Untouched = never contacted, nothing planned — which is precisely the API's
       "new" column, so the chip reuses it instead of inventing a second rule. An
       explicit stage filter still wins. */
    kanban_column: appliedFilter.stage || (dueFilter === "untouched" ? "new" : ""),
    /* Same rule on both sides now: date <= today, and a lead with no date is not
       due (it is simply new). So the API filters, and the count below comes from
       due_counts — both cover the whole set instead of the loaded rows. */
    due_filter: dueFilter === "due" ? "due" : "",
    limit: viewMode === "kanban" ? BOARD_PAGE : LIST_PAGE,
  });

  useEffect(() => { if (isError) toast.error("Failed to load assigned leads"); }, [isError]);

  /* Unread inbound mail per lead. The lead endpoint carries no mail data, so the
     counts are overlaid from the inbox endpoint. */
  const markLeadRead = useMarkLeadRead();
  const { data: unreadMap = {} } = useLeadUnreadCounts(
    seesEveryone || teamIds.includes(",") ? "" : currentUserId(),
  );
  /* Leads the user has opened in this session. The server can lag behind the
     mark-read call, and the refetch that follows would resurrect the badge — so
     a lead stays read here until a NEW inbound mail arrives for it. */
  const [readLeads, setReadLeads] = useState(() => new Set());
  const listSentinelRef = useRef(null);
  const unreadOf = (id) =>
    readLeads.has(String(id)) ? 0 : unreadMap[String(id)]?.unread || 0;

  /* Webhook → SSE → Redux. When a lead replies, refresh the badges and the list
     immediately instead of waiting for the next navigation. */
  const queryClient = useQueryClient();
  const lastInboxEvent = useSelector(selectLastInboxEvent);
  useEffect(() => {
    if (!lastInboxEvent || lastInboxEvent.type !== "inbound" || lastInboxEvent.inbox !== "lead") return;
    const eventLeadId = lastInboxEvent.lead_id ?? lastInboxEvent.customer_id;
    if (eventLeadId) {
      /* If that lead's thread is open right now, the reply was read as it landed —
         keep it marked read (and tell the server) instead of raising a badge the
         user would find waiting after pressing Back. Otherwise the fresh reply
         makes the lead unread again, so the local override is dropped. */
      if (String(eventLeadId) === String(selectedLeadId)) {
        setReadLeads((prev) => new Set(prev).add(String(eventLeadId)));
        markLeadRead.mutate(eventLeadId);
      } else {
        setReadLeads((prev) => {
          if (!prev.has(String(eventLeadId))) return prev;
          const next = new Set(prev);
          next.delete(String(eventLeadId));
          return next;
        });
      }
    }
    queryClient.invalidateQueries({ queryKey: ["lead-unread"], refetchType: "all" });
  }, [lastInboxEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  const pages = data?.pages || [];
  const first = pages[0] || null;
  const total = first?.total || 0;

  /* Every loaded page, flattened. Duplicates are guarded against because a lead
     can shift between pages while the user scrolls. */
  const allLeads = useMemo(() => {
    const byId = new Map();
    pages.forEach((pg) => (pg?.data || []).forEach((row) => byId.set(row.id, row)));
    return Array.from(byId.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const hasMore = Boolean(hasNextPage);

  /* Due & Overdue means a follow-up date that has ARRIVED — today or earlier.
     A lead with no date is not due: it is simply new, and it already stands out
     in the New column. Counting those here only inflated the chip (12 untouched
     leads made "Due 14" out of a real 2). This matches the API's due_filter, so
     the filtering itself is done server-side; this is only the fallback count. */
  const isDueOrOverdue = (lead) => {
    /* Converted leads are closed — they are never "due". Read the column
       directly: classifyLead is declared below and this runs during render. */
    if (String(lead?.kanban_column ?? "").trim().toLowerCase() === "done") return false;
    const nfd = lead.next_followup_date;
    if (!nfd) return false;
    const d = dayjs(nfd, "DD-MM-YYYY");
    if (!d.isValid()) return false;
    return !d.isAfter(dayjs().startOf("day"), "day");
  };
  /* Chips read `chip_counts`: the base set, i.e. every filter except the chips'
     own — that is what keeps the three numbers steady while switching between
     them. `due_counts` is the older shape; the local count is the last resort. */
  const chip = first?.chipCounts || null;
  const allCount = chip?.all ?? first?.dueCounts?.all ?? total ?? allLeads.length;
  const dueCount =
    chip?.due ?? first?.dueCounts?.due ?? allLeads.filter(isDueOrOverdue).length;
  const untouchedCount = chip?.untouched ?? (dueFilter === "all" ? first?.counts?.new ?? null : null);

  /* ── Kanban ────────────────────────────────────────────────────────────────
     Classification comes entirely from the lead row. `status` holds a
     lead_follow_up_status option_value and stays NULL until someone logs a
     follow-up, so "has any value" == contacted — no per-lead fetch needed. */
  /* The column comes from the API (`kanban_column`), which is the single source
     of truth: status, follow-up date and outbound counts are all weighed there,
     so the board and every other screen agree. `status` is only a label on the
     card — it never decides placement. */
  const classifyLead = (item) => {
    const col = String(item?.kanban_column ?? "").trim().toLowerCase();
    return KANBAN_KEYS.has(col) ? col : "new";
  };

  /* A converted lead is a live customer — it must not be deletable, and it has
     nothing to transfer (the subscription belongs to the customer, not to a
     rep's queue). So it disappears from the list while either mode is armed. */
  /* Both chips are server filters now — nothing is filtered out here except the
     converted leads that must not be ticked in delete / transfer mode. */
  const hidesConverted = selectMode === "delete" || selectMode === "transfer";
  const leads = hidesConverted
    ? allLeads.filter((l) => classifyLead(l) !== "done")
    : allLeads;
  const hiddenConvertedCount = hidesConverted ? allLeads.length - leads.length : 0;

  /* Changing a filter changes the query key, so the infinite query starts from
     page 1 on its own — no manual reset needed. */

  // Crossing the sentinel pulls the next page.
  useEffect(() => {
    const node = listSentinelRef.current;
    if (!node || !hasMore || isFetchingNextPage) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchNextPage();
      },
      { rootMargin: "200px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, isFetchingNextPage, fetchNextPage]);

  /* Rows for the table: same set as the board, grouped by stage. Within a stage
     the API's own order is kept (newest first). */
  const listLeads = useMemo(
    () =>
      [...leads].sort(
        (a, b) =>
          (STAGE_RANK[classifyLead(a)] ?? 99) - (STAGE_RANK[classifyLead(b)] ?? 99),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [leads],
  );

  const kanbanColumns = useMemo(() => {
    const buckets = Object.fromEntries(KANBAN_COLUMNS.map((c) => [c.key, []]));
    leads.forEach((item) => {
      const key = classifyLead(item);
      if (buckets[key]) buckets[key].push(item);
    });
    /* `counts` describes what the board is SHOWING: every filter applied except
       the column filter itself. The chips have their own totals (chip_counts), so
       these can follow the view without freezing the chips. Falls back to counting
       the loaded rows only when the API sends no counts at all. */
    const counts = first?.counts || null;
    return KANBAN_COLUMNS.map((c) => ({
      ...c,
      items: buckets[c.key],
      count: counts && counts[c.key] != null ? counts[c.key] : buckets[c.key].length,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads, first, dueFilter]);

  /* Pick the new owner first, then tick the leads. Ticking cards on the board is
     fiddly, so choosing a user drops you into List view. */
  const handleTransferTargetSelected = (user) => {
    setTransferTo(user);
    setShowTransferModal(false);
    setSelectedIds([]);
    setSelectMode("transfer");
    setViewMode("list");
  };

  /* Every bulk action ticks rows the same way — first click arms the mode and
     switches to List (cards have no checkboxes), second click runs it. */
  const armMode = (mode) => {
    setSelectMode(mode);
    setSelectedIds([]);
    if (mode !== "transfer") setTransferTo(null);
    setViewMode("list");
  };
  const clearMode = () => { setSelectMode(null); setSelectedIds([]); setTransferTo(null); };

  /* Take over one lead from a teammate. Same endpoint as Transfer, target is
     always the logged-in user — following up on someone else's lead would send
     their name in the signature, so ownership moves first. */
  const isOwnedByMe = (row) => {
    const owner = row?.assign_to ?? row?.assignToId ?? null;
    return owner == null || owner === "" || String(owner) === String(currentUserId());
  };

  /* Mark a lead negative straight from the Inbox — a rep who calls and gets a
     flat "no" should not have to open the follow-up screen to close it. */
  const handleMarkNegative = (row) => {
    Swal.fire({
      title: "Mark as Negative?",
      html: `<b>${row.name || "This lead"}</b> will move to the Negative list.`,
      input: "textarea",
      inputLabel: "Remark",
      inputPlaceholder: "Why is this lead negative?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Mark Negative",
      confirmButtonColor: "#dc2626",
      reverseButtons: true,
    }).then((r) => {
      if (!r.isConfirmed) return;
      updateStatus.mutate(
        { id: row.id, status: LEAD_STATUS.NEGATIVE, message: r.value || "" },
        {
          onSuccess: () =>
            Swal.fire({
              icon: "success",
              title: "Marked Negative",
              text: `${row.name || "The lead"} moved to the Negative list.`,
              confirmButtonColor: "#0891b2",
            }),
          onError: (err) =>
            Swal.fire("Error", err?.message || "Could not update this lead.", "error"),
        },
      );
    });
  };

  const handleTakeOver = (row) => {
    Swal.fire({
      title: "Take over this lead?",
      html: `<b>${row.name || "This lead"}</b> is assigned to <b>${row.assign_to_name || "another user"}</b>.<br/><br/>It will be assigned to you, and your follow-ups go out under your name.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, take over",
      confirmButtonColor: "#0891b2",
      reverseButtons: true,
    }).then((r) => {
      if (!r.isConfirmed) return;
      transferLeads.mutate(
        { adminId: currentUserId(), leadIds: [row.id] },
        {
          onSuccess: () =>
            Swal.fire({
              icon: "success",
              title: "Lead is yours",
              text: `${row.name || "The lead"} is now assigned to you.`,
              confirmButtonColor: "#0891b2",
            }),
          onError: (err) =>
            Swal.fire("Error", err?.message || "Could not take over this lead.", "error"),
        },
      );
    });
  };

  const requireSelection = () => {
    if (selectedIds.length) return true;
    Swal.fire({ icon: "warning", title: "No leads selected", text: "Tick at least one lead first.", confirmButtonColor: "#0891b2" });
    return false;
  };

  const handleExportClick = async () => {
    if (selectMode !== "export") { armMode("export"); return; }
    if (!requireSelection()) return;
    try {
      setExportLoading(true);
      // Pull the rows straight from the API so the export is not limited to the
      // page on screen — then keep only what the user ticked.
      const raw = await fetchExportEnquiries({
        search: appliedFilter.search || "",
        assign_to: assignScope,
        is_assigned: 1,
        start_date: appliedFilter.start_date || "",
        end_date: appliedFilter.end_date || "",
      });
      const picked = raw.filter((item) => selectedIds.includes(item.id));
      const source = picked.length ? picked : leads.filter((l) => selectedIds.includes(l.id));
      if (!source.length) {
        Swal.fire({ icon: "error", title: "No data", text: "Could not find data for the selected leads." });
        return;
      }
      const rows = source.map((item) => ({
        Name: item.name || "", Email: item.email || "", Phone: item.phone || "",
        "Additional Phone": phonesText(item.additional_phone),
        Company: item.company || "", Message: item.message || "",
        "Lead Status": fuLabelMap[String(item.status ?? "")] || "New",
        "Next Follow-up": item.next_followup_date || "",
        "Assigned To": item.assign_to_name || "Unassigned",
        Date: item.created_date || moment(item.created_at).format("DD-MM-YYYY"),
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [{ wch: 22 }, { wch: 28 }, { wch: 16 }, { wch: 16 }, { wch: 22 }, { wch: 40 }, { wch: 18 }, { wch: 16 }, { wch: 18 }, { wch: 14 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Inbox Leads");
      const filename = `inbox-leads-${moment().format("YYYY-MM-DD")}.xlsx`;
      XLSX.writeFile(wb, filename);
      toast.success(`Exported ${source.length} lead(s) to ${filename}`);
      clearMode();
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  const handleDeleteClick = () => {
    if (selectMode !== "delete") { armMode("delete"); return; }
    if (!requireSelection()) return;
    Swal.fire({
      title: "Are you sure?",
      text: `Delete ${selectedIds.length} selected lead(s)?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc2626",
      reverseButtons: true,
    }).then((r) => {
      if (!r.isConfirmed) return;
      deleteEnquiry.mutate(selectedIds, {
        onSuccess: () => {
          Swal.fire({ icon: "success", title: "Deleted!", text: "Selected leads deleted.", confirmButtonColor: "#0891b2" });
          clearMode();
        },
        onError: (err) => Swal.fire("Error", err?.message || "Failed to delete.", "error"),
      });
    });
  };

  const handleFileChange = (e) => { setFile(e.target.files[0]); setFileError(""); };
  const handleImport = () => {
    if (!file) { setFileError("File is required"); return; }
    importMutation.mutate(file, {
      onSuccess: () => { setImportOpen(false); setFile(null); },
    });
  };

  const handleConfirmTransfer = () => {
    if (!transferTo) { toast.error("Please select an internal user first."); return; }
    if (selectedIds.length === 0) {
      Swal.fire({ icon: "warning", title: "No leads selected", text: "Tick at least one lead to transfer.", confirmButtonColor: "#0891b2" });
      return;
    }
    const limit = transferTo.lead_limit ?? 0;
    const alreadyHas = transferTo.total_assigned_leads ?? 0;
    if (limit > 0 && alreadyHas + selectedIds.length > limit) {
      const canTake = Math.max(0, limit - alreadyHas);
      Swal.fire({
        icon: "error",
        title: "Lead Limit Exceeded",
        html: `<p><strong>${transferTo.name}</strong> has a lead limit of <strong>${limit}</strong>.</p>
               <p>Currently assigned: <strong>${alreadyHas}</strong> &nbsp;·&nbsp; You're trying to add: <strong>${selectedIds.length}</strong></p>
               <p>Can still accept: <strong style="color:${canTake > 0 ? "#16a34a" : "#dc2626"}">${canTake}</strong> more lead(s).</p>`,
        confirmButtonColor: "#0891b2",
      });
      return;
    }
    Swal.fire({
      title: "Confirm Transfer",
      html: `Transfer <strong>${selectedIds.length}</strong> lead(s) to <strong>${transferTo.name}</strong>?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Transfer",
      confirmButtonColor: "#0891b2",
      reverseButtons: true,
    }).then((r) => {
      if (!r.isConfirmed) return;
      transferLeads.mutate(
        { adminId: transferTo.id, leadIds: selectedIds },
        {
          onSuccess: () => {
            Swal.fire({
              icon: "success",
              title: "Transferred!",
              text: `${selectedIds.length} lead(s) moved to ${transferTo.name}.`,
              confirmButtonColor: "#0891b2",
            });
            setSelectedIds([]);
            setTransferTo(null);
          },
          onError: (err) =>
            Swal.fire("Error", err?.message || "Failed to transfer leads.", "error"),
        },
      );
    });
  };

  /* ── follow-up status labels ── */
  const { data: fuOptsRaw = [] } = useFollowUpOptions();
  const fuOpts = Array.isArray(fuOptsRaw) ? fuOptsRaw : (fuOptsRaw?.data || []);
  const fuLabelMap = Object.fromEntries(fuOpts.map((o) => [String(o.option_value), o.label]));

  /* Opening a lead marks its mail read, so the badge clears on the way back. */
  const openLead = (row) => {
    const id = typeof row === "object" ? row.id : row;
    /* A converted lead is a customer now — its story continues on the customer
       profile, not in the follow-up panel. */
    if (typeof row === "object" && classifyLead(row) === "done" && row.customer_id) {
      navigate(`/cms/manageUserData/user-profile/${row.customer_id}`);
      return;
    }
    setSelectedLeadId(id);
    if (unreadOf(id) > 0) {
      setReadLeads((prev) => new Set(prev).add(String(id)));
      markLeadRead.mutate(id);
    }
  };

  /* ── lead detail → open EnquiryFollowUp ── */
  const { data: leadDetails } = useLeadDetailsFollowUp(selectedLeadId);
  useEffect(() => {
    if (!leadDetails) return;
    setFollowUpEnquiry({
      id:             leadDetails.id,
      name:           leadDetails.name,
      email:          leadDetails.email,
      phone:          leadDetails.phone,
      additional_phone:          leadDetails.additional_phone || "",
      company:        leadDetails.company,
      address:        leadDetails.address,
      message:        leadDetails.message,
      status:
        leadDetails.is_positive === 1 ? "Positive"
        : leadDetails.is_positive === 2 ? "Negative"
        : "Neutral",
      assignToId:      leadDetails.assign_to      || null,
      assignedToName:  leadDetails.assign_to_name || "Unassigned",
      stage:           leadDetails.stage_name,
      followUps:       leadDetails.follow_ups     || [],
      // Attribution is carried through to Add Customer when the lead
      // converts, so it must survive this mapping.
      utm_source:    leadDetails.utm_source    || "",
      utm_medium:    leadDetails.utm_medium    || "",
      utm_campaign:  leadDetails.utm_campaign  || "",
    });
  }, [leadDetails]);

  /* ── back-button closes panel ── */
  useEffect(() => {
    if (!followUpEnquiry) return;
    window.history.pushState({ fuOpen: true }, "");
    const onPop = () => setFollowUpEnquiry(null);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [followUpEnquiry]);

  /* ── helpers ── */
  const activeFilterCount = [
    appliedFilter.stage,
    appliedFilter.assign_to,
    appliedFilter.start_date,
    appliedFilter.end_date,
  ].filter(Boolean).length;

  /* Applying a filter changes the query key, so the infinite query restarts at
     page 1 by itself — nothing to reset here. */
  const handleApply = () => setAppliedFilter({ ...filter });
  const handleClear = () => {
    setFilter({ ...EMPTY_FILTER });
    setAppliedFilter({ ...EMPTY_FILTER });
      };

  /* ── follow-up due badge ── */
  const getDueBadge = (nfd) => {
    if (!nfd) return { label: "No date", bg: "#f9fafb", color: "#9ca3af", border: "#e5e7eb" };
    const d = dayjs(nfd, "DD-MM-YYYY");
    if (!d.isValid()) return { label: "No date", bg: "#f9fafb", color: "#9ca3af", border: "#e5e7eb" };
    const today = dayjs().startOf("day");
    if (d.isBefore(today)) return { label: "Overdue", bg: "#fef2f2", color: "#dc2626", border: "#fecaca" };
    if (d.isSame(today))   return { label: "Due Today", bg: "#fffbeb", color: "#d97706", border: "#fde68a" };
    return { label: d.format("DD-MM-YYYY"), bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" };
  };

  /* ── demo booking block (Kanban card) ──
     Rendered only for leads that actually carry a booking, so a normal lead's
     card keeps its current height and the board drag is untouched. */
  const renderDemoBlock = (item) => {
    /* Once the lead is converted the subscription below is the story — an old
       demo booking is history, and its Join / Reschedule buttons are noise. */
    if (!hasDemo(item) || classifyLead(item) === "done") return null;
    const stop = (e) => e.stopPropagation();   // the whole card opens the lead
    return (
      <div
        style={{
          paddingTop: 8, marginBottom: 8, borderTop: "1px solid #f3f4f6",
          display: "flex", flexDirection: "column", gap: 5,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <DemoStatusPill lead={item} size={10} />
          {isDemoOverdue(item) && <OverdueDemoHint />}
        </div>
        <div style={{ fontSize: 11.5, color: "#4b5563", fontWeight: 600 }}>
          Demo: {formatDemoWhen(item)}
        </div>
        {(canJoinDemo(item) || canRescheduleDemo(item)) && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {canJoinDemo(item) && (
              <button
                onClick={(e) => { stop(e); openTeamsMeeting(item); }}
                style={{ padding: "2px 10px", borderRadius: 20, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#2563eb", fontSize: 10, fontWeight: 700, cursor: "pointer" }}
              >
                Join Teams
              </button>
            )}
            {canRescheduleDemo(item) && (
              <button
                onClick={(e) => { stop(e); setReschedulingLead(item); }}
                style={{ padding: "2px 10px", borderRadius: 20, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", fontSize: 10, fontWeight: 700, cursor: "pointer" }}
              >
                Reschedule
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  /* ── columns ── */
  const columns = [
    {
      title: <span style={{ ...thStyle }}>#</span>,
      key: "index", width: 50, align: "center",
      render: (_, __, idx) => (
        <span style={{ fontSize: 13, color: "#9ca3af", fontWeight: 600 }}>
          {idx + 1}
        </span>
      ),
    },
    {
      title: <span style={thStyle}>Lead Name</span>,
      key: "name",
      render: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg,#0891b2,#0e7490)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            {(row.name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div
              onClick={() => openLead(row)}
              style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, color: "#0891b2", cursor: "pointer", fontSize: 14 }}
            >
              {row.name || "—"}
              {unreadOf(row.id) > 0 && (
                <Tooltip title={`${unreadOf(row.id)} unread repl${unreadOf(row.id) === 1 ? "y" : "ies"}`}>
                  <span style={{ background: "#ef4444", color: "#fff", borderRadius: 20, minWidth: 18, height: 18, padding: "0 6px", fontSize: 10, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                    {unreadOf(row.id)}
                  </span>
                </Tooltip>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              {row.company && <span style={{ fontSize: 11, color: "#9ca3af" }}>{row.company}</span>}
              {/* The booking lives in its own Demo column now. */}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: <span style={thStyle}>Email</span>,
      dataIndex: "email", key: "email",
      render: (v) => <span style={{ fontSize: 13, color: "#374151" }}>{v || "—"}</span>,
    },
    {
      title: <span style={thStyle}>Source</span>,
      key: "utm_source",
      // Where the lead came from — the same value that is carried over to the
      // customer on conversion.
      render: (_, row) =>
        row.utm_source ? (
          <span style={{ background: "#f5f3ff", color: "#7c3aed", border: "1px solid #ddd6fe", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
            {row.utm_source}
          </span>
        ) : (
          <span style={{ fontSize: 13, color: "#9ca3af" }}>—</span>
        ),
    },
    {
      title: <span style={thStyle}>Phone</span>,
      key: "phone",
      /* A number must never wrap — "+91" on one line and the digits on the next
         reads as two numbers. The width keeps the column from being squeezed by
         its neighbours once every action button is on screen. */
      width: 170,
      // Primary (green dot) + alternate number (blue dot) when the lead has one
      render: (_, row) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {row.phone ? (
            <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "#374151", fontWeight: 600, whiteSpace: "nowrap" }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#16a34a", flexShrink: 0 }} />
              {withCountryCode(row.country_code, row.phone)}
            </span>
          ) : (
            <span style={{ fontSize: 13, color: "#9ca3af" }}>—</span>
          )}
          <AdditionalPhones
            value={row.additional_phone}
            dot
            max={2}
            itemStyle={{ fontSize: 12 }}
          />
        </div>
      ),
    },
    {
      // "Lead Status", not "Status" — Demo Bookings shows a different one
      title: <span style={thStyle}>Lead Status</span>,
      key: "lead_status",
      /* A rep's own marking (Hot Lead, Interested…) when there is one. "0" is the
         value a lead is created with, not a choice — showing its label made brand
         new leads read "Follow Up", and a null one showed a bare dash. In both of
         those cases the stage is the honest answer. */
      render: (_, row) => {
        const val = String(row?.status ?? "");
        const hasMarking = val !== "" && val !== "0" && !!fuLabelMap[val];
        if (hasMarking) {
          const s = FOLLOWUP_STATUS_STYLE[val] || FOLLOWUP_STATUS_STYLE["0"];
          return (
            <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
              {fuLabelMap[val]}
            </span>
          );
        }
        const col = KANBAN_COLUMNS.find((c) => c.key === classifyLead(row));
        if (!col) return <span style={{ fontSize: 12, color: "#9ca3af" }}>—</span>;
        return (
          <span style={{ background: col.bg, color: col.color, border: `1px solid ${col.border}`, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
            {col.label}
          </span>
        );
      },
    },
    {
      title: <span style={thStyle}>Stage</span>,
      key: "stage",
      /* The board's column for this lead, straight from `kanban_column`. This
         used to read follow_up_status / enquiry_status, which the list API always
         sends as null — so it defaulted to "0" and every single row claimed
         "Follow Up", whatever the lead actually was. */
      render: (row) => {
        const key = classifyLead(row);
        const col = KANBAN_COLUMNS.find((c) => c.key === key);
        if (!col) return <span style={{ fontSize: 13, color: "#9ca3af" }}>—</span>;
        return (
          <span style={{ background: col.bg, color: col.color, border: `1px solid ${col.border}`, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600 }}>
            {col.label}
          </span>
        );
      },
    },
    {
      title: <span style={thStyle}>Next Follow-up</span>,
      dataIndex: "next_followup_date", key: "next_followup_date",
      render: (v, r) => {
        /* A converted lead has nothing left to follow up on, so an old date
           must not keep reading as "Overdue" here — the Kanban card drops the
           chip for the same reason. */
        if (classifyLead(r) === "done")
          return <span style={{ fontSize: 13, color: "#9ca3af" }}>—</span>;
        const b = getDueBadge(v);
        return (
          <span style={{ background: b.bg, color: b.color, border: `1px solid ${b.border}`, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
            {b.label}
          </span>
        );
      },
    },
    {
      title: <span style={thStyle}>Demo</span>,
      key: "demo",
      /* Only booked leads have anything here; the rest stay visibly empty, and a
         converted lead's old booking is dropped for the same reason the board
         drops it — the subscription has replaced it. */
      render: (row) =>
        !hasDemo(row) || classifyLead(row) === "done" ? (
          <span style={{ fontSize: 13, color: "#d1d5db" }}>—</span>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
              <DemoStatusPill lead={row} size={10} />
              {isDemoOverdue(row) && <OverdueDemoHint />}
            </div>
            <span style={{ fontSize: 12, color: "#4b5563", fontWeight: 600, whiteSpace: "nowrap" }}>
              {formatDemoWhen(row)}
            </span>
          </div>
        ),
    },
    {
      title: <span style={thStyle}>Assigned To</span>,
      dataIndex: "assign_to_name", key: "assign_to_name",
      width: 150,
      render: (v) => (
        <span style={{ fontSize: 13, color: "#374151", whiteSpace: "nowrap" }}>{v || "—"}</span>
      ),
    },
    {
      title: <span style={thStyle}>Action</span>,
      key: "action",
      // The buttons below are already centred — this lines the header up with them.
      align: "center",
      /* Enough room for the widest combination (View Follow-up + Join Teams +
         Reschedule + 👎); without it the first button wrapped onto two lines and
         the row grew taller than the rest. */
      width: 330,
      render: (row) => (
        <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "center" }}>
          <Tooltip title="View Follow-up">
            <button
              onClick={() => openLead(row)}
              style={{ padding: "5px 14px", borderRadius: 8, border: "1px solid #bae6fd", background: "#f0f9ff", color: "#0891b2", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
            >
              View Follow-up
            </button>
          </Tooltip>
          {canJoinDemo(row) && classifyLead(row) !== "done" && (
            <Tooltip title="Join the Teams meeting for this demo">
              <button
                onClick={(e) => { e.stopPropagation(); openTeamsMeeting(row); }}
                style={{ padding: "5px 12px", borderRadius: 8, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#2563eb", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
              >
                Join Teams
              </button>
            </Tooltip>
          )}
          {canRescheduleDemo(row) && classifyLead(row) !== "done" && (
            <Tooltip title="Move this demo to another slot">
              <button
                onClick={(e) => { e.stopPropagation(); setReschedulingLead(row); }}
                style={{ padding: "5px 12px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
              >
                Reschedule
              </button>
            </Tooltip>
          )}
          {classifyLead(row) !== "negative" && classifyLead(row) !== "done" && (
            <Tooltip title="Mark Negative">
              <button
                onClick={(e) => { e.stopPropagation(); handleMarkNegative(row); }}
                disabled={updateStatus.isPending}
                style={{ padding: "5px 10px", borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", fontSize: 12, fontWeight: 600, cursor: updateStatus.isPending ? "not-allowed" : "pointer" }}
              >
                👎
              </button>
            </Tooltip>
          )}
          {mayTransfer && !isOwnedByMe(row) && (
            <Tooltip title={`Assigned to ${row.assign_to_name || "someone else"} — take it over to follow up`}>
              <button
                onClick={(e) => { e.stopPropagation(); handleTakeOver(row); }}
                disabled={transferLeads.isPending}
                style={{ padding: "5px 12px", borderRadius: 8, border: "1px solid #fde68a", background: "#fffbeb", color: "#b45309", fontSize: 12, fontWeight: 600, cursor: transferLeads.isPending ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}
              >
                Take over
              </button>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* ── Header ── */}
          <div style={{ background: "linear-gradient(135deg,#0891b2 0%,#0e7490 100%)", borderRadius: 16, padding: "28px 32px", marginBottom: 24, boxShadow: "0 8px 32px rgba(8,145,178,0.28)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 56, height: 56, background: "rgba(255,255,255,0.18)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>
                <InboxOutlined style={{ color: "white" }} />
              </div>
              <div>
                <h4 style={{ color: "white", margin: 0, fontSize: 24, fontWeight: 700 }}>Inbox</h4>
                <p style={{ color: "rgba(255,255,255,0.85)", margin: 0, fontSize: 14 }}>
                  Your assigned leads with pending or overdue follow-ups
                </p>
              </div>
            </div>
            {total > 0 && (
              <span style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 20, padding: "5px 16px", fontSize: 14, fontWeight: 700 }}>
                {total} leads
              </span>
            )}
          </div>

          {/* ── Filters ── */}
          <div style={{ background: "white", borderRadius: 14, border: "1px solid #e5e7eb", boxShadow: "0 2px 12px rgba(0,0,0,0.05)", marginBottom: 20, overflow: "hidden" }}>
            <div
              onClick={() => setFiltersOpen((o) => !o)}
              style={{ padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", background: "#fafbff", borderBottom: filtersOpen ? "1px solid #e5e7eb" : "none" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <FilterOutlined style={{ color: "#0891b2", fontSize: 16 }} />
                <span style={{ fontWeight: 700, fontSize: 15, color: "#1f2937" }}>Filters</span>
                {activeFilterCount > 0 && (
                  <span style={{ background: "#0891b2", color: "white", borderRadius: 20, padding: "1px 10px", fontSize: 12, fontWeight: 700 }}>
                    {activeFilterCount} active
                  </span>
                )}
              </div>
              <span style={{ color: "#6b7280", fontSize: 13 }}>{filtersOpen ? "▲ Collapse" : "▼ Expand"}</span>
            </div>

            {filtersOpen && (
              <div style={{ padding: "20px 24px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 16 }}>
                  <Input
                    prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                    placeholder="Search name, email, phone..."
                    value={filter.search}
                    onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
                    style={{ borderRadius: 9, height: 40 }}
                    allowClear
                  />
                  {/* Stage filter mirrors the Kanban columns — is_positive
                      (Positive/Neutral/Negative) is a different thing and was
                      never the lead's status. */}
                  <Select
                    value={filter.stage || undefined}
                    // Applies straight away like the search box — no "Apply Filters"
                    // click. Only the stage key is merged, so an already applied
                    // date range stays put.
                    onChange={(v) => {
                      const stage = v || "";
                      setFilter((f) => ({ ...f, stage }));
                      setAppliedFilter((p) => ({ ...p, stage }));
                                          }}
                    placeholder="All Stages"
                    allowClear
                    style={{ width: "100%", height: 40 }}
                    options={KANBAN_COLUMNS.map((c) => ({ value: c.key, label: c.label }))}
                  />
                  {/* Internal user — the list is the team's pipeline, so this
                      narrows it to one member. Only shown when there is someone
                      besides the logged-in user to narrow to. */}
                  {teamMembers.length > 1 && (
                    <Select
                      value={filter.assign_to || undefined}
                      onChange={(v) => {
                        const assign_to = v || "";
                        setFilter((f) => ({ ...f, assign_to }));
                        setAppliedFilter((p) => ({ ...p, assign_to }));
                                              }}
                      placeholder={seesEveryone ? "All Internal Users" : "All My Team"}
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      style={{ width: "100%", height: 40 }}
                      options={teamMembers.map((u) => ({
                        value: String(u.id),
                        label: String(u.id) === String(currentUserId()) ? `${u.name} (Me)` : u.name,
                      }))}
                    />
                  )}
                  <DatePicker
                    placeholder="Start Date"
                    format="DD/MM/YYYY"
                    value={filter.start_date ? dayjs(filter.start_date, "YYYY-MM-DD") : null}
                    onChange={(_, s) => setFilter((f) => ({ ...f, start_date: s ? dayjs(s, "DD/MM/YYYY").format("YYYY-MM-DD") : null }))}
                    style={{ width: "100%", height: 40, borderRadius: 9 }}
                    suffixIcon={<CalendarOutlined style={{ color: "#9ca3af" }} />}
                  />
                  <DatePicker
                    placeholder="End Date"
                    format="DD/MM/YYYY"
                    value={filter.end_date ? dayjs(filter.end_date, "YYYY-MM-DD") : null}
                    onChange={(_, s) => setFilter((f) => ({ ...f, end_date: s ? dayjs(s, "DD/MM/YYYY").format("YYYY-MM-DD") : null }))}
                    style={{ width: "100%", height: 40, borderRadius: 9 }}
                    suffixIcon={<CalendarOutlined style={{ color: "#9ca3af" }} />}
                  />
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button onClick={handleClear} style={{ height: 38, padding: "0 20px", borderRadius: 9, border: "1px solid #e5e7eb", background: "white", color: "#6b7280", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                    <ClearOutlined /> Clear
                  </button>
                  <button onClick={handleApply} style={{ height: 38, padding: "0 24px", borderRadius: 9, border: "none", background: "linear-gradient(135deg,#0891b2,#0e7490)", color: "white", fontWeight: 700, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, boxShadow: "0 2px 8px rgba(8,145,178,0.3)" }}>
                    <FilterOutlined /> Apply Filters
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── Table ── */}
          <div style={{ background: "white", borderRadius: 16, boxShadow: "0 2px 16px rgba(0,0,0,0.07)", border: "1px solid #f0f0f0", overflow: "hidden" }}>
            <div style={{ padding: "18px 24px", borderBottom: "2px solid #f5f5f5", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 38, height: 38, background: "linear-gradient(135deg,#0891b2,#0e7490)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 17 }}>
                <UserOutlined />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#1f2937" }}>
                  {seesEveryone ? "All Assigned Leads" : teamMembers.length > 1 ? "My Team's Leads" : "My Assigned Leads"}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {total} lead{total !== 1 ? "s" : ""}
                  {seesEveryone
                    ? " — every assigned lead"
                    : teamMembers.length > 1
                      ? ` — yours + ${teamMembers.length - 1} team member${teamMembers.length - 1 !== 1 ? "s" : ""}`
                      : " — assigned to you"}
                </div>
              </div>

              {/* Due filter — the list is the full pipeline; this narrows it to today's work */}
              <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end", alignItems: "center" }}>
                {[
                  { key: "all", label: "All", count: allCount },
                  { key: "due", label: "Due & Overdue", count: dueCount },
                  { key: "untouched", label: "Untouched", count: untouchedCount ?? "—" },
                ].map((chip) => {
                  const active = dueFilter === chip.key;
                  return (
                    <button
                      key={chip.key}
                      onClick={() => setDueFilter(chip.key)}
                      style={{
                        padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                        cursor: "pointer",
                        border: `1px solid ${active ? "#0891b2" : "#e5e7eb"}`,
                        background: active ? "#0891b2" : "#fff",
                        color: active ? "#fff" : "#6b7280",
                        display: "inline-flex", alignItems: "center", gap: 6,
                      }}
                    >
                      {chip.label}
                      <span style={{
                        background: active ? "rgba(255,255,255,0.25)" : "#f3f4f6",
                        color: active ? "#fff" : "#9ca3af",
                        borderRadius: 20, padding: "0 7px", fontSize: 11, fontWeight: 700,
                      }}>
                        {chip.count}
                      </span>
                    </button>
                  );
                })}

                {/* View toggle — Kanban is the default, same as the old inbox */}
                <div style={{ display: "flex", border: "1px solid #0891b2", borderRadius: 8, overflow: "hidden", marginLeft: 4 }}>
                  {[
                    { key: "kanban", label: "Kanban" },
                    { key: "list", label: "List" },
                  ].map((v) => (
                    <button
                      key={v.key}
                      onClick={() => {
                        /* Kanban asks for a 200-row page, the list for 10 — so a
                           page number carried over from the list (page 2 of 10)
                           lands past the end of a 200-row page and the board comes
                           back empty. Start from page 1 on every switch. */
                        setViewMode(v.key);
                                              }}
                      style={{
                        padding: "5px 14px", border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer",
                        background: viewMode === v.key ? "#0891b2" : "#fff",
                        color: viewMode === v.key ? "#fff" : "#0891b2",
                      }}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>

                {/* Transfer — only super-admin / admin / manager can move a lead
                    from one internal user to another. */}
                {mayTransfer && (
                  <>
                    {transferTo && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#ecfeff", border: "1px solid #a5f3fc", color: "#0e7490", borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 600 }}>
                        <TeamOutlined /> {transferTo.name}
                        <span style={{ background: "#cffafe", borderRadius: 10, padding: "1px 7px", fontSize: 11 }}>
                          {transferTo.total_assigned_leads ?? 0} / {transferTo.lead_limit ?? 0}
                        </span>
                        <button
                          onClick={clearMode}
                          title="Cancel transfer"
                          style={{ border: "none", background: "none", color: "#0e7490", cursor: "pointer", fontWeight: 700, padding: 0, lineHeight: 1 }}
                        >
                          ✕
                        </button>
                      </span>
                    )}
                    <button
                      onClick={transferTo ? handleConfirmTransfer : () => setShowTransferModal(true)}
                      disabled={transferLeads.isPending}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "linear-gradient(135deg,#0891b2,#0e7490)", color: "#fff", borderRadius: 8, padding: "6px 16px", fontWeight: 600, fontSize: 12, border: "none", cursor: transferLeads.isPending ? "not-allowed" : "pointer", opacity: transferLeads.isPending ? 0.7 : 1, marginLeft: 4 }}
                    >
                      <TeamOutlined />
                      {transferLeads.isPending
                        ? "Transferring…"
                        : transferTo
                          ? `Confirm Transfer${selectedIds.length ? ` (${selectedIds.length})` : ""}`
                          : "Transfer Leads"}
                    </button>
                  </>
                )}

                {/* Import — bulk upload, same endpoint as All Leads */}
                {(
                  <button
                    onClick={() => setImportOpen((o) => !o)}
                    style={{ ...toolbarBtn, background: "#fff", color: "#0891b2", border: "1px solid #0891b2", outline: importOpen ? "2px solid #0891b2" : "none", outlineOffset: 2 }}
                  >
                    <UploadOutlined /> Import
                  </button>
                )}

                {/* Export — everyone may take their own list out */}
                <button
                  onClick={handleExportClick}
                  disabled={exportLoading}
                  style={{ ...toolbarBtn, background: "linear-gradient(135deg,#06b6d4,#0284c7)", color: "#fff", cursor: exportLoading ? "not-allowed" : "pointer", opacity: exportLoading ? 0.7 : 1, outline: selectMode === "export" ? "2px solid #0284c7" : "none", outlineOffset: 2 }}
                >
                  <DownloadOutlined />
                  {exportLoading
                    ? "Exporting…"
                    : selectMode === "export"
                      ? `Export Selected${selectedIds.length ? ` (${selectedIds.length})` : ""}`
                      : "Export"}
                </button>

                {mayManage && (
                  <button
                    onClick={handleDeleteClick}
                    disabled={deleteEnquiry.isPending}
                    style={{ ...toolbarBtn, background: "linear-gradient(135deg,#ef4444,#dc2626)", color: "#fff", cursor: deleteEnquiry.isPending ? "not-allowed" : "pointer", opacity: deleteEnquiry.isPending ? 0.7 : 1, outline: selectMode === "delete" ? "2px solid #dc2626" : "none", outlineOffset: 2 }}
                  >
                    <DeleteOutlined />
                    {selectMode === "delete"
                      ? `Confirm Delete${selectedIds.length ? ` (${selectedIds.length})` : ""}`
                      : "Delete Selected"}
                  </button>
                )}

                {(
                  <Link
                    to="/cms/manageenquiry/add-enquiry"
                    style={{ ...toolbarBtn, background: "linear-gradient(135deg,#52c41a,#389e0d)", color: "#fff", textDecoration: "none" }}
                  >
                    <PlusOutlined /> Add Lead
                  </Link>
                )}

                {selectMode && (
                  <button onClick={clearMode} style={{ ...toolbarBtn, background: "#fff", color: "#6b7280", border: "1px solid #e5e7eb" }}>
                    Cancel
                  </button>
                )}
              </div>
            </div>

            {importOpen && (
              <div style={{ padding: "14px 24px", background: "#f8fafc", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Import leads (.xlsx):</span>
                <input type="file" onChange={handleFileChange} className={`form-control ${fileError ? "is-invalid" : ""}`} style={{ borderRadius: 8, maxWidth: 320 }} />
                <button
                  onClick={handleImport}
                  disabled={importMutation.isPending}
                  style={{ ...toolbarBtn, background: "linear-gradient(135deg,#0891b2,#0e7490)", color: "#fff", cursor: importMutation.isPending ? "not-allowed" : "pointer", opacity: importMutation.isPending ? 0.7 : 1 }}
                >
                  {importMutation.isPending ? "Uploading…" : "Upload"}
                </button>
                {/* Same sample sheet as All Leads — the importer is the same endpoint */}
                <a
                  href="/assets/images/import_enquiry_format_trademark.xlsx"
                  download
                  style={{ ...toolbarBtn, background: "#fff", color: "#0891b2", border: "1px solid #0891b2", textDecoration: "none" }}
                >
                  <DownloadOutlined /> Format
                </a>
                {fileError && <span style={{ color: "#dc2626", fontSize: 12, fontWeight: 600 }}>{fileError}</span>}
                {/* country_code is mandatory now — leads are no longer India-only */}
                <span style={{ fontSize: 11, color: "#6b7280", flexBasis: "100%" }}>
                  Required columns: <b>name</b>, <b>phone</b> (without country code), <b>country_code</b> (e.g. +91), <b>email</b>. Download Format for the exact sheet.
                </span>
              </div>
            )}

            {selectMode && selectMode !== "transfer" && (
              <div style={{ padding: "10px 24px", background: selectMode === "delete" ? "#fef2f2" : "#ecfeff", borderBottom: `1px solid ${selectMode === "delete" ? "#fecaca" : "#a5f3fc"}`, color: selectMode === "delete" ? "#b91c1c" : "#0e7490", fontSize: 13 }}>
                <strong>{selectMode === "delete" ? "Delete mode:" : "Export mode:"}</strong>&nbsp;
                tick the leads, then click <strong>{selectMode === "delete" ? "Confirm Delete" : "Export Selected"}</strong>.
                {hiddenConvertedCount > 0 && (
                  <> &nbsp;·&nbsp; {hiddenConvertedCount} converted lead{hiddenConvertedCount !== 1 ? "s" : ""} hidden (already customers).</>
                )}
              </div>
            )}

            {transferTo && (
              <div style={{ padding: "10px 24px", background: "#ecfeff", borderBottom: "1px solid #a5f3fc", color: "#0e7490", fontSize: 13 }}>
                <strong>Transfer mode:</strong>&nbsp;tick the leads to move to <strong>{transferTo.name}</strong>, then click <strong>Confirm Transfer</strong>.
                {hiddenConvertedCount > 0 && (
                  <> &nbsp;·&nbsp; {hiddenConvertedCount} converted lead{hiddenConvertedCount !== 1 ? "s" : ""} hidden (already customers).</>
                )}
              </div>
            )}

            {/* Skeleton only on a cold load. A background refetch (SSE badge
                refresh, window focus) must not blank the screen — that reads as
                a page reload to the user. */}
            {isFetching && allLeads.length === 0 ? (
              <div style={{ padding: 24 }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} active paragraph={{ rows: 1 }} style={{ marginBottom: 14 }} />
                ))}
              </div>
            ) : leads.length === 0 ? (
              <div style={{ padding: "60px 24px", textAlign: "center" }}>
                <Empty description={<span style={{ color: "#9ca3af" }}>No assigned leads found</span>} />
              </div>
            ) : viewMode === "kanban" ? (
              /* ═══ KANBAN VIEW ═══ */
              <div style={{ padding: "20px 24px", overflowX: "auto" }}>
                <div style={{ display: "flex", gap: 16, alignItems: "flex-start", minWidth: "fit-content" }}>
                  {kanbanColumns.map((col) => (
                    <div key={col.key} style={{ flex: "0 0 300px", background: "#f8fafc", borderRadius: 14, border: `1px solid ${col.border}`, overflow: "hidden" }}>
                      {/* Column header */}
                      <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 8, borderBottom: `2px solid ${col.border}`, background: "#fff" }}>
                        <span style={{ background: col.bg, color: col.color, border: `1px solid ${col.border}`, borderRadius: 20, padding: "3px 12px", fontSize: 12, fontWeight: 700 }}>
                          {col.label}
                        </span>
                        <span style={{ background: col.color, color: "#fff", borderRadius: "50%", width: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>
                          {col.count}
                        </span>
                      </div>

                      {/* Cards */}
                      <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10, maxHeight: 620, overflowY: "auto" }}>
                        {col.items.length === 0 ? (
                          <div style={{ textAlign: "center", padding: "32px 16px", color: "#d1d5db", fontSize: 13 }}>No leads</div>
                        ) : col.items.map((item) => {
                          const due = getDueBadge(item.next_followup_date);
                          const st = String(item.status ?? "");
                          /* "0" is the value a lead is created with, not something a
                             rep chose — showing its label made every New card claim
                             "Follow Up". With no real marking the card shows the
                             stage instead ("New"); once a rep marks the lead, that
                             marking is shown, because the column already says the
                             stage. */
                          const hasMarking = st !== "" && st !== "0" && !!fuLabelMap[st];
                          const stLabel = hasMarking ? fuLabelMap[st] : col.label;
                          const stStyle = hasMarking
                            ? FOLLOWUP_STATUS_STYLE[st] || FOLLOWUP_STATUS_STYLE["0"]
                            : { bg: col.bg, color: col.color, border: col.border };
                          return (
                            <div
                              key={item.id}
                              onClick={() => openLead(item)}
                              style={{ background: "#fff", borderRadius: 12, padding: "14px 14px 10px", border: "1px solid #f0f0f0", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", cursor: "pointer", transition: "box-shadow 0.15s" }}
                              onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.1)")}
                              onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.05)")}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                                <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,#0891b2,#0e7490)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                                  {(item.name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                                </div>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600, fontSize: 13, color: "#111827" }}>
                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name || "—"}</span>
                                    {unreadOf(item.id) > 0 && (
                                      <Tooltip title={`${unreadOf(item.id)} unread repl${unreadOf(item.id) === 1 ? "y" : "ies"}`}>
                                        <span style={{ background: "#ef4444", color: "#fff", borderRadius: 20, minWidth: 18, height: 18, padding: "0 6px", fontSize: 10, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                          {unreadOf(item.id)}
                                        </span>
                                      </Tooltip>
                                    )}
                                  </div>
                                  {item.company && (
                                    <div style={{ fontSize: 11, color: "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.company}</div>
                                  )}
                                </div>
                              </div>

                              <div style={{ fontSize: 12, color: "#6b7280", display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
                                {item.email && <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>✉ {item.email}</div>}
                                {item.phone && <div style={{ fontWeight: 600, color: "#374151" }}>📞 {withCountryCode(item.country_code, item.phone)}</div>}
                                <AdditionalPhones value={item.additional_phone} max={2} />
                                {/* The board spans the whole team, so the owner
                                    has to be readable straight off the card. */}
                                <div style={{ fontSize: 11, color: item.assign_to_name ? "#0e7490" : "#9ca3af", display: "flex", alignItems: "center", gap: 5 }}>
                                  <UserOutlined style={{ fontSize: 10 }} />
                                  <span style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {String(item.assign_to) === String(currentUserId())
                                      ? "Me"
                                      : item.assign_to_name || "Unassigned"}
                                  </span>
                                </div>
                                {mayTransfer && !isOwnedByMe(item) && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleTakeOver(item); }}
                                    disabled={transferLeads.isPending}
                                    style={{ alignSelf: "flex-start", padding: "2px 10px", borderRadius: 20, border: "1px solid #fde68a", background: "#fffbeb", color: "#b45309", fontSize: 10, fontWeight: 700, cursor: transferLeads.isPending ? "not-allowed" : "pointer" }}
                                  >
                                    Take over
                                  </button>
                                )}
                                {item.utm_source && (
                                  <div style={{ fontSize: 11, color: "#7c3aed", display: "flex", alignItems: "center", gap: 5 }}>
                                    <span style={{ fontSize: 10 }}>🌐</span>
                                    <span style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {item.utm_source}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {renderDemoBlock(item)}

                              {/* Converted leads carry their subscription — on the
                                  Done column that is the useful information, not the
                                  next follow-up date. */}
                              {col.key === "done" && item.subscription && (
                                <div style={{ paddingTop: 8, marginBottom: 8, borderTop: "1px solid #f3f4f6", display: "flex", flexDirection: "column", gap: 3 }}>
                                  <div style={{ fontSize: 12, fontWeight: 700, color: "#111827" }}>
                                    {item.subscription.plan_name || "—"}
                                    <span style={{ marginLeft: 6, color: "#16a34a" }}>
                                      ₹{Number(item.subscription.paid_amount ?? item.subscription.plan_price ?? 0).toLocaleString("en-IN")}
                                    </span>
                                  </div>
                                  <div style={{ fontSize: 11, color: "#6b7280" }}>
                                    {item.subscription.start_date ? dayjs(item.subscription.start_date).format("DD MMM YYYY") : "—"}
                                    {" → "}
                                    {item.subscription.end_date ? dayjs(item.subscription.end_date).format("DD MMM YYYY") : "—"}
                                  </div>
                                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                                    {item.subscription.plan_type === "free_trial" && (
                                      <span style={{ background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe", borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 700 }}>
                                        Trial
                                      </span>
                                    )}
                                    {item.subscription.payment_status &&
                                      // "trial" here would just repeat the chip above
                                      String(item.subscription.payment_status).toLowerCase() !== "trial" && (
                                      <span style={{ background: "#f5f5f5", color: "#4b5563", borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 700, textTransform: "capitalize" }}>
                                        {item.subscription.payment_status}
                                      </span>
                                    )}
                                    {item.subscription.subscription_status && (
                                      <span
                                        style={{
                                          borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 700, textTransform: "capitalize",
                                          background: item.subscription.subscription_status === "active" ? "#f0fdf4" : "#fef2f2",
                                          color: item.subscription.subscription_status === "active" ? "#16a34a" : "#dc2626",
                                          border: "1px solid " + (item.subscription.subscription_status === "active" ? "#bbf7d0" : "#fecaca"),
                                        }}
                                      >
                                        {item.subscription.subscription_status}
                                      </span>
                                    )}
                                  </div>
                                  {item.subscription.order_id && (
                                    <div style={{ fontSize: 10, color: "#9ca3af" }}>{item.subscription.order_id}</div>
                                  )}
                                </div>
                              )}

                              {/* Follow-up chips are pointless once the lead is
                                  converted — the subscription above says everything. */}
                              {col.key !== "done" && (
                              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", paddingTop: 10, borderTop: "1px solid #f3f4f6" }}>
                                <span style={{ background: due.bg, color: due.color, border: `1px solid ${due.border}`, borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 700 }}>
                                  {due.label}
                                </span>
                                {stLabel && (
                                  <span style={{ background: stStyle.bg, color: stStyle.color, border: `1px solid ${stStyle.border}`, borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 600 }}>
                                    {stLabel}
                                  </span>
                                )}
                                {item.last_contacted_at && (
                                  <Tooltip title={`Last contacted ${apiTime(item.last_contacted_at).format("DD MMM YYYY, hh:mm A")}`}>
                                    <span style={{ background: "#f8fafc", color: "#64748b", border: "1px solid #e2e8f0", borderRadius: 20, padding: "2px 9px", fontSize: 10, fontWeight: 600 }}>
                                      ✉ {apiTime(item.last_contacted_at).fromNow()}
                                    </span>
                                  </Tooltip>
                                )}
                                {/* The demo block above carries the booking now —
                                    date, status, and the Teams actions. */}
                              </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <Table
                  dataSource={listLeads}
                  columns={columns}
                  rowKey="id"
                  pagination={false}
                  loading={false}
                  // Checkboxes only while a bulk action is armed
                  rowSelection={
                    selectMode
                      ? {
                          selectedRowKeys: selectedIds,
                          onChange: (keys) => setSelectedIds(keys),
                        }
                      : undefined
                  }
                  onRow={(row) => ({
                    style: { cursor: "pointer" },
                    onClick: () => {
                      // While a bulk action is armed a row click ticks the box
                      // instead of opening the lead
                      if (selectMode) {
                        setSelectedIds((prev) =>
                          prev.includes(row.id)
                            ? prev.filter((id) => id !== row.id)
                            : [...prev, row.id],
                        );
                        return;
                      }
                      openLead(row);
                    },
                    onMouseEnter: (e) => { e.currentTarget.style.background = "#f0f9ff"; },
                    onMouseLeave: (e) => { e.currentTarget.style.background = ""; },
                  })}
                  style={{ borderRadius: 0 }}
                  size="middle"
                  /* The Demo and Action columns pushed the total past the
                     viewport, and Ant's answer to that is to squeeze cells until
                     phone numbers break across lines. Scroll the table instead. */
                  scroll={{ x: 1400 }}
                />
              </div>
            )}

            {/* Infinite scroll — the sentinel asks the API for the next page. */}
            {leads.length > 0 && (
              <div ref={listSentinelRef} style={{ padding: "16px 24px", borderTop: "1px solid #f0f0f0", textAlign: "center", background: "#fafbff" }}>
                {hasMore ? (
                  <span style={{ fontSize: 13, color: "#0e7490", fontWeight: 600 }}>
                    {isFetchingNextPage
                      ? "Loading more leads…"
                      : `Showing ${leads.length} of ${total} — scroll for more`}
                  </span>
                ) : (
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>
                    All {leads.length} lead{leads.length !== 1 ? "s" : ""} loaded
                  </span>
                )}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── Pick who to transfer the leads to ── */}
      {showTransferModal && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowTransferModal(false); }}
          style={{ position: "fixed", inset: 0, zIndex: 1200, background: "rgba(15,23,42,.55)", backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
        >
          <div style={{ background: "#fff", borderRadius: 18, width: "100%", maxWidth: 580, overflow: "hidden", boxShadow: "0 20px 60px rgba(8,145,178,.25)", border: "2px solid #cffafe" }}>
            <div style={{ background: "linear-gradient(135deg,#0891b2,#0e7490)", padding: "16px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 34, height: 34, background: "rgba(255,255,255,.2)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 16 }}>
                  <TeamOutlined />
                </div>
                <div>
                  <div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>Transfer Leads</div>
                  <div style={{ color: "rgba(255,255,255,.8)", fontSize: 11 }}>Move leads to another internal user</div>
                </div>
              </div>
              <button
                onClick={() => setShowTransferModal(false)}
                style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(255,255,255,.2)", border: "none", color: "#fff", cursor: "pointer", fontSize: 14 }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: "20px 22px", maxHeight: "60vh", overflowY: "auto" }}>
              {teamMembers.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}>
                  <TeamOutlined style={{ fontSize: 40, marginBottom: 10, display: "block" }} />
                  <div>No internal users found</div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {teamMembers.map((user) => {
                    const used = user.total_assigned_leads ?? 0;
                    const limit = user.lead_limit ?? 0;
                    const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
                    const overLimit = limit > 0 && used >= limit;
                    return (
                      <div
                        key={user.id}
                        onClick={() => { if (!overLimit) handleTransferTargetSelected(user); }}
                        title={overLimit ? `${user.name} has reached their lead limit (${limit})` : ""}
                        style={{
                          border: `1.5px solid ${overLimit ? "#fecaca" : "#cffafe"}`,
                          background: overLimit ? "#fff5f5" : "#f8feff",
                          borderRadius: 12, padding: "12px 14px",
                          cursor: overLimit ? "not-allowed" : "pointer",
                          opacity: overLimit ? 0.7 : 1,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,#0891b2,#0e7490)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                              {user.name?.split(" ").map((w) => w.charAt(0)).join("").slice(0, 2).toUpperCase() || "?"}
                            </div>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 14, color: "#1f2937" }}>{user.name}</div>
                              <div style={{ fontSize: 12, color: "#6b7280" }}>{user.email}</div>
                            </div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <span style={{ background: overLimit ? "#fef2f2" : "#ecfeff", color: overLimit ? "#dc2626" : "#0e7490", border: `1px solid ${overLimit ? "#fecaca" : "#a5f3fc"}`, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>
                              {used} / {limit} leads
                            </span>
                            {overLimit && (
                              <div style={{ fontSize: 11, color: "#dc2626", marginTop: 3, fontWeight: 600 }}>Limit reached</div>
                            )}
                          </div>
                        </div>
                        <div style={{ background: "#f3f4f6", borderRadius: 99, height: 6, overflow: "hidden" }}>
                          <div style={{ height: "100%", borderRadius: 99, width: `${pct}%`, background: pct >= 100 ? "linear-gradient(90deg,#ef4444,#dc2626)" : pct >= 80 ? "linear-gradient(90deg,#f59e0b,#d97706)" : "linear-gradient(90deg,#22d3ee,#0891b2)", transition: "width .4s ease" }} />
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 11, color: "#9ca3af" }}>
                          <span>Role: <strong>{user.role}</strong></span>
                          <span>{pct}% used &nbsp;·&nbsp; {Math.max(0, limit - used)} remaining</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{ padding: "14px 22px", borderTop: "1px solid #f0f0f0", background: "#fafafa", display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowTransferModal(false)}
                style={{ padding: "8px 22px", background: "#f1f5f9", color: "#6b7280", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── EnquiryFollowUp panel — full-screen overlay above the app chrome,
             same wrapper All Leads / Inbox / Future use. Without it the panel
             renders inside .main-content and the sidebar clips its left column. */}
      {followUpEnquiry && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1050, background: "#f1f5f9", animation: "slideInRight .28s ease" }}>
          <EnquiryFollowUp
            enquiry={followUpEnquiry}
            onClose={() => {
              setFollowUpEnquiry(null);
              setSelectedLeadId(null);
              /* Anything done inside the panel (status change, mail, call, note)
                 can move the lead to another column. The mutations invalidate the
                 lists themselves, but closing the panel is the moment the user
                 looks at the board again — so refetch here too and the board is
                 never one action behind. */
              queryClient.invalidateQueries({ queryKey: ["inbox-leads"], refetchType: "all" });
              queryClient.invalidateQueries({ queryKey: ["enquiryList"], refetchType: "all" });
              queryClient.invalidateQueries({ queryKey: ["lead-unread"], refetchType: "all" });
            }}
          />
        </div>
      )}

      {reschedulingLead && (
        <RescheduleDemoModal
          lead={reschedulingLead}
          onClose={() => setReschedulingLead(null)}
        />
      )}
    </div>
  );
}
